import { useTranslation } from 'react-i18next'
import { getHomeContent } from '../../content/homeContent'
import trackEvent from '../../utils/trackEvent'
import HeroOrbit from './HeroOrbit'
import HeroCanvas from './HeroCanvas'
import '../../input.css'

const HeroSection = () => {
  const { i18n } = useTranslation()
  const content = getHomeContent(i18n.language).hero

  return (
    <section
      id="hero-section"
      className="relative flex w-full min-h-screen isolate bg-transparent text-white"
    >
      {/* ── Background ───────────────────────────────────────────── */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        {/* Physics canvas — colliding orbs, fills with bg color itself */}
        <HeroCanvas />

        {/* Dot grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '36px 36px',
          }}
        />

        {/* Bottom fade into next section */}
        <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[#11161c] to-transparent" />
      </div>

      {/* ── Content ──────────────────────────────────────────────── */}
      <div className="relative z-10 flex w-full px-6 pb-20 pt-24 md:px-14 md:pt-28">
        <div className="mx-auto flex min-h-[calc(100vh-9rem)] w-full max-w-6xl items-center">
          <div className="grid w-full items-center gap-12 lg:grid-cols-[1.02fr_0.98fr] lg:gap-10">

            {/* ── Left: the pitch ───────────────────────────────── */}
            <div className="hero-stagger text-left">
              <p className="text-xs uppercase tracking-[0.4em] text-pyramid-1/80">
                {content.eyebrow}
              </p>

              <h1 className="mt-6 font-Quicksand text-[clamp(2.4rem,4.6vw,3.9rem)] font-bold leading-[1.02] tracking-[-0.03em] text-white">
                {content.headline}{' '}
                <span className="text-[#4cbfe0]">{content.headlineAccent}</span>
              </h1>

              <p className="mt-6 max-w-xl text-lg leading-8 text-slate-300/90">
                {content.body}
              </p>

              {/* CTAs */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="/services"
                  onClick={() => trackEvent('hero_portfolio_click')}
                  className="inline-flex items-center justify-center gap-2 rounded-[10px] bg-gradient-to-r from-pyramid-1 via-pyramid-10 to-pyramid-19 px-7 py-3.5 font-semibold text-ebony shadow-lg shadow-pyramid-10/30 transition-all hover:-translate-y-0.5 hover:shadow-pyramid-10/50"
                >
                  {content.primaryCta}
                  <span aria-hidden="true">&#8594;</span>
                </a>
                <a
                  href="/how-we-work"
                  onClick={() => trackEvent('hero_how_click')}
                  className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-white/15 bg-white/[0.04] px-7 py-3.5 font-semibold text-white transition-all hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/[0.08]"
                >
                  {content.secondaryCta}
                  <span aria-hidden="true">&#8594;</span>
                </a>
              </div>

              {/* Speed to value, the question every buyer asks second */}
              <div className="mt-5 flex flex-wrap items-center gap-2.5">
                {content.speed.map((item) => (
                  <span
                    key={item}
                    className="inline-flex items-center gap-2 rounded-full border border-[#19a2cf]/25 bg-[#19a2cf]/[0.06] px-3.5 py-1.5 text-[13px] text-[#cfe4f5]"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-pyramid-10" aria-hidden="true" />
                    {item}
                  </span>
                ))}
              </div>

              {/* Reassurance line, directly under the action. The proof numbers
                  live once, in the foundation section further down. */}
              <p className="mt-3 text-sm text-slate-400">{content.reassurance}</p>

            </div>

            {/* ── Right: your operation, with our pod around it ─── */}
            <div className="relative">
              <HeroOrbit />
            </div>

          </div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection
